<script setup lang="ts">
import { computed } from "vue";
import { UiButton, UiText } from "@shopexperts/shared";
import type { AdminProjectDetail } from "@shopexperts/legacy-api";
import AdminParticipantClientCard from "./AdminParticipantClientCard.vue";
import AdminParticipantExpertCard from "./AdminParticipantExpertCard.vue";

const props = defineProps<{
  project: AdminProjectDetail;
  bordered?: boolean;
}>();

const emit = defineEmits<{
  (e: "add-participant"): void;
  (e: "remove-participant", participantId: number): void;
}>();

const clientParticipants = computed(() =>
  props.project.participants.filter((participant) => participant.participant_group === "client"),
);

const expertParticipants = computed(() =>
  props.project.participants.filter((participant) => participant.participant_group === "expert"),
);
</script>

<template>
  <div
    class="bg-white rounded-xl overflow-hidden flex flex-col max-h-full"
    :class="props.bordered ? 'border border-gray-200' : ''"
  >
    <div class="flex-1 overflow-y-auto p-5">
      <div class="flex items-start justify-between gap-3">
        <UiText as="h3" variant="p" class="font-semibold text-gray-900">
          Participants
        </UiText>
        <span class="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold leading-4 tracking-wide bg-blue-50 text-blue-700 border border-blue-200 shrink-0 capitalize">
          {{ project.project_status.replace(/_/g, " ") }}
        </span>
      </div>

      <div class="mt-5 space-y-3">
        <UiText as="p" variant="p-small" class="text-gray-500">
          Clients
        </UiText>
        <AdminParticipantClientCard
          v-for="participant in clientParticipants"
          :key="participant.id"
          :name="participant.organization.name || `Org #${participant.org_id}`"
          :subtitle="participant.participant_role"
          :removable="project.admin_actions.can_remove_participant"
          @remove="emit('remove-participant', participant.id)"
        />
      </div>

      <div class="mt-5 space-y-3">
        <UiText as="p" variant="p-small" class="text-gray-500">
          Experts
        </UiText>
        <AdminParticipantExpertCard
          v-for="participant in expertParticipants"
          :key="participant.id"
          :name="participant.organization.name || `Org #${participant.org_id}`"
          :role="participant.participant_role"
          :removable="project.admin_actions.can_remove_participant"
          @remove="emit('remove-participant', participant.id)"
        />
      </div>

      <div class="mt-5">
        <UiButton
          class="w-full"
          variant="primary"
          label="Add Participant"
          @click="emit('add-participant')"
        />
      </div>
    </div>
  </div>
</template>
